/**
 * The country page opens where you left it (user, 12/10/2026: "by default it
 * shows the latest opened"): the campaign group and the view (Board / Day
 * timeline / Live) last open for that country, in this browser. Only when
 * the link names nothing itself — a link with ?group=, ?tab=, ?container=…
 * still wins. A remembered group that no longer exists is dropped; the
 * tutorial is never remembered. Storage is a convenience: when it is
 * unavailable the page opens on the Board, every group, as before.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useEffect, useRef } from 'react';

import type { CountryTab } from '../../CountryTabs';

export interface LastView {
  tab: CountryTab;
  /** The open campaign group's key; null = every group. */
  group: string | null;
}

const STORAGE_KEY = 'trafficpulse-automation-last-view';

function readAll(): Record<string, LastView> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, LastView>) : {};
  } catch {
    return {};
  }
}

export const readLastView = (countryKey: string): LastView | null => readAll()[countryKey] ?? null;

export function saveLastView(countryKey: string, view: LastView): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readAll(), [countryKey]: view }));
  } catch {
    // Private window or blocked storage: nothing is remembered, nothing breaks.
  }
}

interface Options {
  countryKey: string;
  /** False when the page is embedded or its plan has not loaded yet. */
  ready: boolean;
  /** The campaign groups the page can open now. */
  groupKeys: string[];
  /** What the page shows now. */
  current: LastView;
  /** The link already says what to show — nothing is restored. */
  linkChose: boolean;
  restore: (view: LastView) => void;
}

export function useLastView({ countryKey, ready, groupKeys, current, linkChose, restore }: Options): void {
  const { tab, group } = current;
  const restoredFor = useRef<string | null>(null);
  const skipSave = useRef(false);
  useEffect(() => {
    if (!ready || restoredFor.current === countryKey) return;
    restoredFor.current = countryKey;
    const last = linkChose ? null : readLastView(countryKey);
    if (!last) return;
    skipSave.current = true;
    restore({ tab: last.tab, group: last.group !== null && groupKeys.includes(last.group) ? last.group : null });
  }, [ready, countryKey, linkChose, groupKeys, restore]);
  useEffect(() => {
    if (!ready || restoredFor.current !== countryKey || tab === 'tutorial') return;
    // The render that restored still shows the old view — never save that one.
    if (skipSave.current) {
      skipSave.current = false;
      return;
    }
    saveLastView(countryKey, { tab, group });
  }, [ready, countryKey, tab, group]);
}
